import moment from 'moment';
import { rgba } from 'polished';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ActionButton, Buttons, CancelButton, Icon, ModalCard } from '~/components';
import { useLocale } from '~/hooks';
import { colors, weights } from '~/styles';

// Set how often to check for the maintenance window
// 1 hour in milliseconds
const notificationCheckInterval = 3600000;

// Controls the dismiss action save state in local storage
const maintenanceId = 'maintenance.2024-10-02';

// Maintenance start/end in UTC (include the Z)
const maintenanceStart = '2024-10-03 02:00Z';
const maintenanceEnd = '2024-10-03 04:00Z';

// Get stuff into moment for easy manipulation
const start = moment(maintenanceStart);
const end = moment(maintenanceEnd);
const duration = moment.duration(end.diff(start));

// We're required to give 48 hours notice, set to 72 hours to be safe
const notificationStart = start.clone().subtract(3, 'days');

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background-color: ${rgba(colors.white, 0.1)};
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  width: 9.0625rem;
  cursor: pointer;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
  }
`;

const CardText = styled.p`
  margin: 0;
  color: ${colors.white};
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  line-height: 1.4;
`;

const Description = styled.p`
  &:not(:first-child) {
    margin-top: 1rem;
  }
`;

const HoverLabel = styled.div`
  position: absolute;
  top: 50%;
  left: calc(100% - 0.125rem);
  padding: 0.5rem;
  background-color: ${colors.white};
  border-radius: 0.25rem;
  box-shadow: 0 0 0.625rem ${rgba(colors.black, 0.25)};
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 0.875rem;
  width: max-content;
  max-width: 14rem;
  transform: translateY(-50%);
  pointer-events: none;
  opacity: 0;
  z-index: 95;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -0.375rem;
    width: 0.375rem;
    height: 0.75rem;
    transform: translateY(-50%);
    background-color: ${colors.white};
    clip-path: polygon(100% 0, 100% 100%, 0 50%);
  }
`;

const BadgeWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;

  &:hover ${HoverLabel} {
    opacity: 1;
  }
`;

const Badge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: solid 0.0625rem ${rgba(colors.white, 0.2)};
  border-radius: 0.375rem;
  background-color: ${rgba(colors.white, 0.1)};
  color: ${colors.white};
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: ${rgba(colors.white, 0.15)};
    color: ${colors.white};
  }
`;

function isNotificationActive() {
  const now = moment();
  return now.isAfter(notificationStart) && now.isBefore(end);
}

export default function Maintenance({ collapsed }) {
  const [notificationActive, setNotificationActive] = useState(isNotificationActive());
  const [notificationHidden, setNotificationHidden] = useState(
    localStorage ? localStorage.getItem(maintenanceId) === 'hide' : false,
  );
  const [showAlert, setShowAlert] = useState(false);

  const locale = useLocale();

  // Checks to see if it's needed/unneeded
  useEffect(() => {
    const cb = () => {
      setNotificationActive(isNotificationActive());
    };
    const interval = setInterval(cb, notificationCheckInterval);
    return () => clearInterval(interval);
  }, []);

  const handleClose = () => setShowAlert(false);

  const handleHide = () => {
    if (localStorage) {
      localStorage.setItem(maintenanceId, 'hide');
    }
    setShowAlert(false);
    setNotificationHidden(true);
  };

  if (!notificationActive || notificationHidden) {
    return null;
  }

  return (
    <>
      {collapsed ? (
        <BadgeWrapper>
          <Badge onClick={() => setShowAlert(true)}>
            <Icon icon="screwdriver-wrench" />
          </Badge>
          <HoverLabel>System maintenance has been scheduled. Click for details.</HoverLabel>
        </BadgeWrapper>
      ) : (
        <Card onClick={() => setShowAlert(true)}>
          <CardText>System maintenance has been scheduled.</CardText>
        </Card>
      )}
      {showAlert && (
        <ModalCard
          title={
            <>
              <Icon icon="screwdriver-wrench" /> System Maintenance
            </>
          }
          onClose={handleClose}>
          <ModalCard.Body>
            <Description>
              System maintenance has been scheduled. Ruddr may be inaccessible during this period.
            </Description>
            <Description>
              Start: <strong>{start.locale(locale).format('LLLL')}</strong>
              <br />
              End: <strong>{end.locale(locale).format('LLLL')}</strong>
              <br />
              Duration:{' '}
              <strong>
                {duration.locale(locale).asHours()} {duration.locale(locale).asHours() === 1 ? 'hour' : 'hours'}
              </strong>
            </Description>
            <Description>To dismiss this notification, click the "Hide" button below.</Description>
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={handleHide}>Hide</CancelButton>
              <ActionButton onClick={handleClose}>OK</ActionButton>
            </Buttons>
          </ModalCard.Footer>
        </ModalCard>
      )}
    </>
  );
}
